import * as THREE from 'three';

const MAX_EMBERS = 50;
const MAP_HALF = 88;
const EMBER_Y_MIN = 4;
const EMBER_Y_MAX = 28;
const METEOR_MIN_INTERVAL = 25;
const METEOR_MAX_INTERVAL = 40;
const METEOR_LIFE = 0.55;

type Ember = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  bobPhase: number;
};

type MeteorFx = {
  root: THREE.Group;
  life: number;
};

/**
 * Seasonal ambient FX — floating embers across the map and rare meteor streaks in the sky.
 */
export class FpsSeasonFx {
  private readonly embers: Ember[] = [];
  private meteorCd = this.nextMeteorDelay();
  private meteor: MeteorFx | null = null;
  private readonly emberGeo = new THREE.SphereGeometry(0.055, 5, 5);
  private readonly emberMat = new THREE.MeshBasicMaterial({
    color: 0xff8a3c,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
  });
  private readonly streakGeo = new THREE.CylinderGeometry(0.04, 0.12, 18, 6, 1, true);
  private readonly streakMat = new THREE.MeshBasicMaterial({
    color: 0xffe8cc,
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
  });

  constructor(private readonly scene: THREE.Scene) {
    while (this.embers.length < MAX_EMBERS) {
      this.spawnEmber(true);
    }
  }

  update(dt: number, camera?: THREE.Camera): void {
    this.tickEmbers(dt, camera);
    this.tickMeteor(dt);
  }

  dispose(): void {
    for (const e of this.embers) {
      this.scene.remove(e.mesh);
      if (e.mesh.material !== this.emberMat) {
        (e.mesh.material as THREE.Material).dispose();
      }
    }
    this.embers.length = 0;
    if (this.meteor) {
      this.scene.remove(this.meteor.root);
      this.disposeMeteorGroup(this.meteor.root);
      this.meteor = null;
    }
    this.emberGeo.dispose();
    this.emberMat.dispose();
    this.streakGeo.dispose();
    this.streakMat.dispose();
  }

  private tickEmbers(dt: number, camera?: THREE.Camera): void {
    while (this.embers.length < MAX_EMBERS) {
      this.spawnEmber(false);
    }

    for (const e of this.embers) {
      e.bobPhase += dt * (1.6 + Math.sin(e.bobPhase) * 0.35);
      e.mesh.position.addScaledVector(e.vel, dt);
      e.mesh.position.y += Math.sin(e.bobPhase) * dt * 0.35;

      const { x, y, z } = e.mesh.position;
      if (
        Math.abs(x) > MAP_HALF ||
        Math.abs(z) > MAP_HALF ||
        y < EMBER_Y_MIN - 2 ||
        y > EMBER_Y_MAX + 4
      ) {
        this.respawnEmber(e);
        continue;
      }

      if (camera) {
        e.mesh.lookAt(camera.position);
      }

      const mat = e.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.52 + Math.sin(e.bobPhase * 1.4) * 0.18;
    }
  }

  private tickMeteor(dt: number): void {
    if (this.meteor) {
      this.meteor.life += dt;
      const t = this.meteor.life / METEOR_LIFE;
      for (const child of this.meteor.root.children) {
        if (child instanceof THREE.Mesh) {
          const mat = child.material as THREE.MeshBasicMaterial;
          mat.opacity = (1 - t) * (child.userData.baseOpacity as number);
        }
      }
      if (this.meteor.life >= METEOR_LIFE) {
        this.scene.remove(this.meteor.root);
        this.disposeMeteorGroup(this.meteor.root);
        this.meteor = null;
        this.meteorCd = this.nextMeteorDelay();
      }
      return;
    }

    this.meteorCd -= dt;
    if (this.meteorCd <= 0) {
      this.spawnMeteorStreak();
    }
  }

  private spawnEmber(_initial: boolean): void {
    const mesh = new THREE.Mesh(this.emberGeo, this.emberMat.clone());
    const x = (Math.random() - 0.5) * MAP_HALF * 2;
    const z = (Math.random() - 0.5) * MAP_HALF * 2;
    const y = EMBER_Y_MIN + Math.random() * (EMBER_Y_MAX - EMBER_Y_MIN);
    mesh.position.set(x, y, z);
    const mat = mesh.material as THREE.MeshBasicMaterial;
    mat.color.setHSL(0.06 + Math.random() * 0.04, 0.92, 0.52 + Math.random() * 0.12);
    mat.opacity = 0.55 + Math.random() * 0.25;
    this.scene.add(mesh);
    this.embers.push({
      mesh,
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 2.4,
        0.08 + Math.random() * 0.22,
        (Math.random() - 0.5) * 2.4,
      ),
      bobPhase: Math.random() * Math.PI * 2,
    });
  }

  private respawnEmber(e: Ember): void {
    const edge = Math.floor(Math.random() * 4);
    const y = EMBER_Y_MIN + Math.random() * (EMBER_Y_MAX - EMBER_Y_MIN);
    let x = 0;
    let z = 0;
    const span = MAP_HALF * 0.92;
    if (edge === 0) {
      x = -span;
      z = (Math.random() - 0.5) * span * 2;
    } else if (edge === 1) {
      x = span;
      z = (Math.random() - 0.5) * span * 2;
    } else if (edge === 2) {
      z = -span;
      x = (Math.random() - 0.5) * span * 2;
    } else {
      z = span;
      x = (Math.random() - 0.5) * span * 2;
    }
    e.mesh.position.set(x, y, z);
    e.vel.set(
      (Math.random() - 0.5) * 2.6,
      0.06 + Math.random() * 0.2,
      (Math.random() - 0.5) * 2.6,
    );
    e.bobPhase = Math.random() * Math.PI * 2;
  }

  private spawnMeteorStreak(): void {
    const root = new THREE.Group();
    const angle = Math.random() * Math.PI * 2;
    const dist = 20 + Math.random() * 55;
    const cx = Math.cos(angle) * dist;
    const cz = Math.sin(angle) * dist;
    const skyY = 46 + Math.random() * 16;
    root.position.set(cx, skyY, cz);

    const streak = new THREE.Mesh(this.streakGeo, this.streakMat.clone());
    streak.rotation.z = Math.PI * 0.5 + (Math.random() - 0.5) * 0.35;
    streak.rotation.y = angle + Math.PI * 0.5 + (Math.random() - 0.5) * 0.5;
    streak.userData.baseOpacity = 0.95;
    root.add(streak);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.28, 8, 8),
      new THREE.MeshBasicMaterial({
        color: 0xfff0d0,
        transparent: true,
        opacity: 0.98,
        depthWrite: false,
      }),
    );
    head.position.set(8.5, 0, 0);
    head.userData.baseOpacity = 0.98;
    streak.add(head);

    this.scene.add(root);
    this.meteor = { root, life: 0 };
  }

  private disposeMeteorGroup(root: THREE.Group): void {
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    });
  }

  private nextMeteorDelay(): number {
    return METEOR_MIN_INTERVAL + Math.random() * (METEOR_MAX_INTERVAL - METEOR_MIN_INTERVAL);
  }
}
